import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { AlertTriangle, X } from "lucide-react";
import { api } from "../../services/api";

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

/**
 * ChainIntegrityBanner — Phase 1 Track G.6 per ADR-PE-10.
 *
 * Customer-facing red banner that fires the moment chain-integrity
 * verification reports a failure. Mounts in DashboardLayout so it persists
 * across pages until either the chain is re-verified clean OR the customer
 * dismisses the banner for the current session.
 *
 * Polls `/api/audit-integrity/snapshot` every 30s. The "court-admissible"
 * marketing claim is process-conditional (Synthesis §8 caveat 12) — this
 * banner backs the claim with continuous, customer-visible verification.
 *
 * Failure detection logic:
 *   - chainStatus.lastVerificationOk === false → fire
 *   - heartbeat.recentVerifications (top 5) contains any ok=false → fire
 *   - heartbeat fetch error → silent (the integrity page surfaces detail)
 *
 * Dismissal: per-session only. After re-verification ok the banner does not
 * re-appear; if the chain breaks again later in the session, banner re-fires.
 *
 * Cardinal Rule #4: banner renders ONLY chain-status flags, NEVER trace
 * payload. The deep-dive page is where rich data goes.
 */

const POLL_INTERVAL_MS = 30 * 1000;

interface IntegritySnapshotMinimal {
  chainStatus: {
    lastVerificationOk: boolean | null;
  };
  heartbeat: {
    recentVerifications: Array<{
      ok: boolean;
      verifiedAt: string;
      brokenReason: string | null;
      scope: "smoke" | "full";
    }>;
  };
}

export function ChainIntegrityBanner() {
  const [snapshot, setSnapshot] = useState<IntegritySnapshotMinimal | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [lastFailureSeen, setLastFailureSeen] = useState<string | null>(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      // Über api.getAuditChainSnapshot() statt rohem apiClient — die Methode
      // trägt den Demo-Mode-Guard (sonst pollt der Banner im Demo-Mode alle
      // 30s einen 500er gegen das Backend, auf JEDER Dashboard-Seite).
      const data = (await api.getAuditChainSnapshot()) as unknown;
      // Shape-guard against backend drift — without this, `{success: true,
      // data: "broken"}` would set snapshot to a string and the render
      // path `snapshot.chainStatus?.lastVerificationOk` would crash the
      // banner (and the whole DashboardLayout it mounts in). cat 27.
      if (
        data
        && typeof data === "object"
        && "chainStatus" in data
        && "heartbeat" in data
      ) {
        setSnapshot(data as IntegritySnapshotMinimal);
      }
    } catch (err) {
      // Silent for end-users — the deep-dive page is the place for fetch
      // error UX. The banner stays in last-known state. DEV log preserves
      // the redacted diagnostic so engineers can trace why the integrity
      // poller stopped updating.
      if (import.meta.env.DEV) {
        console.warn("[ChainIntegrityBanner] snapshot fetch failed:", redactErrorForDev(err));
      }
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const interval = setInterval(fetchSnapshot, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchSnapshot]);

  if (!snapshot) return null;

  const lastVerificationOk = snapshot.chainStatus?.lastVerificationOk;
  const recentFailures = (snapshot.heartbeat?.recentVerifications || [])
    .slice(0, 5)
    .filter((v) => v.ok === false);

  // Council Sprint 2 F-45 — witness-divergence escalation. divergenceDetected
  // is true when any external witness reports a different STH than Adjudon
  // for the same namespace; this is operator-collusion-signal severity and
  // must surface in the same red banner as chain-verification failures.
  // The witnessConsortium object is added by auditIntegrityController; if
  // the backend hasn't shipped it yet (pre-rollout window) the field is
  // undefined and we silently no-op.
  const witnessDivergence = (snapshot as { witnessConsortium?: { divergenceDetected?: boolean; recentMismatches?: Array<{ witnessName?: string; mismatchReason?: string; receivedAt?: string }> } }).witnessConsortium;
  const hasWitnessDivergence = witnessDivergence?.divergenceDetected === true;

  const hasFailure = lastVerificationOk === false || recentFailures.length > 0 || hasWitnessDivergence;

  // Re-fire banner if a NEW failure timestamp appears (i.e., the chain broke
  // again after a previous dismiss). Compare to lastFailureSeen.
  const currentFailureTimestamp = recentFailures[0]?.verifiedAt
    || (lastVerificationOk === false ? "chain-status-failure" : null)
    || (hasWitnessDivergence ? witnessDivergence?.recentMismatches?.[0]?.receivedAt || "witness-divergence" : null);

  const showBanner =
    hasFailure &&
    !(dismissed && currentFailureTimestamp === lastFailureSeen);

  if (!showBanner) return null;

  // Witness-divergence takes precedence over generic chain-verification reason
  // because it's a stronger collusion signal (multiple independent observers
  // disagreeing) than a single-org chain-verify failure.
  const firstWitnessMismatch = witnessDivergence?.recentMismatches?.[0];
  const reason =
    hasWitnessDivergence
      ? `Witness consortium reports STH divergence${firstWitnessMismatch?.witnessName ? ` (${firstWitnessMismatch.witnessName})` : ""}${firstWitnessMismatch?.mismatchReason ? `: ${firstWitnessMismatch.mismatchReason}` : ""}`
      : (recentFailures[0]?.brokenReason ||
        (lastVerificationOk === false ? "Chain verification reported a mismatch" : "Unknown"));

  const handleDismiss = () => {
    setDismissed(true);
    setLastFailureSeen(currentFailureTimestamp);
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="bg-enforcement/10 border-b border-enforcement/30 px-4 py-2.5 flex items-center justify-between gap-3 flex-shrink-0"
      data-testid="chain-integrity-banner"
    >
      <div className="flex items-center gap-2 text-sm text-foreground">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 text-enforcement" aria-hidden="true" />
        <span>
          <span className="font-semibold">Chain integrity check failed.</span>{" "}
          <span className="text-muted-foreground">{reason}</span>{" "}
          <Link
            to="/dashboard/audit/chain?tab=health"
            className="font-semibold underline hover:no-underline text-enforcement"
          >
            View details
          </Link>
        </span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss chain integrity banner"
        className="text-muted-foreground hover:text-foreground flex-shrink-0"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
